import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";

/**
 * Web Vitals → dataLayer → GTM → GA4.
 *
 * Each metric arrives once per page (LCP/FCP/TTFB) or on page unload
 * (CLS/INP cumulative). We push them to dataLayer so GTM forwards them
 * to GA4 as custom events — giving you per-page Core Web Vitals data
 * in GA4 reports without waiting 28 days for Search Console CrUX.
 *
 * Event shape (matches Google's recommended payload):
 *   event: "web-vitals"
 *   metric_name: "LCP" | "FCP" | "CLS" | "INP" | "TTFB"
 *   metric_value: number (ms or unitless for CLS)
 *   metric_id: string (stable per metric instance)
 *   metric_rating: "good" | "needs-improvement" | "poor"
 *   metric_navigation_type: "navigate" | "reload" | "back-forward" | ...
 *   page_path: location.pathname
 */

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function send(metric: Metric): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "web-vitals",
    metric_name: metric.name,
    // CLS is unitless; others are milliseconds. Round to int for clarity.
    metric_value:
      metric.name === "CLS"
        ? Math.round(metric.value * 1000) / 1000
        : Math.round(metric.value),
    metric_id: metric.id,
    metric_rating: metric.rating,
    metric_navigation_type: metric.navigationType,
    page_path: window.location.pathname,
  });
}

/**
 * Start listening for Core Web Vitals. Call once on app boot.
 * Subscribes lazily — no perf cost unless metrics are observed.
 */
export function initWebVitals(): void {
  if (typeof window === "undefined") return;
  onCLS(send);
  onFCP(send);
  onINP(send);
  onLCP(send);
  onTTFB(send);
}
